import { useState } from "react";
import { Button } from "reactstrap";

import { ConfigField, IConfig } from "Pages/Setup/Connect/connectShared";
import { IntegrationDef } from "Pages/Setup/Connect/connectData";
import styles from "Pages/Setup/Connect/Onboarding.module.css";

const PILL: Record<string, [string, string]> = {
    ok: [styles.pillGood, "Configured"],
    prog: [styles.pillWarn, "Needs setup"],
    todo: [styles.pillTodo, "Not configured"],
};

interface Props {
    integration: IntegrationDef;
    find: (suffix: string) => IConfig | undefined;
    onEdit: (suffix: string, secret: boolean, index: number) => void;
    onSave: (suffix: string, value: string) => Promise<boolean>;
    goTo: (view: string) => void;
}

function toBool(value: string | undefined): boolean {
    if (!value) return false;
    return ["true", "1", "yes", "y", "on"].includes(
        value.trim().toLowerCase()
    );
}

export default function IntegrationDetail({
    integration,
    find,
    onEdit,
    onSave,
    goTo,
}: Props) {
    const [saving, setSaving] = useState<Record<string, boolean>>({});
    const showStatus = integration.id !== "default_behavior";

    const [pillClass, pillText] = PILL[integration.status] ?? PILL.todo;

    return (
        <div className={styles.panel}>
            <div className={styles.phead}>
                <div className={`${styles.logo} ${styles.logoInt}`}>⚙</div>
                <div>
                    <div className={styles.pheadName}>{integration.name}</div>
                    <div className={styles.pheadSub}>
                        {integration.direction} · integration detail
                    </div>
                </div>
                {showStatus && (
                    <span className={`${styles.pill} ${pillClass}`}>
                        <span className={styles.dot} /> {pillText}
                    </span>
                )}
            </div>

            <div className={`${styles.sec} ${styles.secLine}`}>
                <h4>What it does</h4>
                <p className={styles.hint} style={{ margin: 0 }}>
                    {integration.io}
                </p>
            </div>

            <div className={styles.sec}>
                <h4>Settings</h4>
                {integration.fields.length === 0 ? (
                    <p className={styles.hint} style={{ margin: 0 }}>
                        No client-facing settings — this integration runs on the
                        system connection and shared Map data.
                    </p>
                ) : integration.fields.every((f) => f.type === "boolean") ? (
                    <div className={styles.boolList}>
                        {integration.fields.map((field) => {
                            const checked = toBool(find(field.suffix)?.keyValue);
                            const isSaving = !!saving[field.suffix];
                            return (
                                <label
                                    className={styles.boolRow}
                                    key={field.suffix}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={isSaving}
                                        onChange={async (e) => {
                                            const next = e.target.checked;
                                            setSaving((prev) => ({
                                                ...prev,
                                                [field.suffix]: true,
                                            }));
                                            await onSave(
                                                field.suffix,
                                                next ? "true" : "false"
                                            );
                                            setSaving((prev) => ({
                                                ...prev,
                                                [field.suffix]: false,
                                            }));
                                        }}
                                    />
                                    <div className={styles.boolMeta}>
                                        <span className={styles.boolLabel}>
                                            {field.label}
                                        </span>
                                        {field.help && (
                                            <span className={styles.boolHelp}>
                                                {field.help}
                                            </span>
                                        )}
                                        <code className={styles.key}>
                                            {field.suffix}
                                        </code>
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                ) : (
                    integration.fields.map((field, index) => (
                        <ConfigField
                            key={field.suffix}
                            field={field}
                            config={find(field.suffix)}
                            index={index}
                            onEdit={onEdit}
                        />
                    ))
                )}
            </div>
        </div>
    );
}
